import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser, NgOptimizedImage } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';

import { HeroComponent } from './hero/hero.component';

import { BlogPost } from '../../models/blog-post.models';
import { BlogService } from '../../services/blog.service';
import { ContentService } from '../../services/content.service';
import { SeoService } from '../../services/seo.service';
import { ImageService } from '../../services/image.service';
import { NewsletterService } from '../../services/newsletter.service';
import { buildSiteUrl, getBlogCategoryDisplayName, siteConfig } from '../../config/site-config';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    HeroComponent,
    RouterLink,
    NgOptimizedImage
  ],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css']
})
export class HomeComponent implements OnInit {
  readonly aboutPath = siteConfig.routes.aboutPath;
  readonly blogPath = siteConfig.routes.blogPath;

  loading = false;
  error = false;

  recentBlogPosts: BlogPost[] = [];
  popularHerbs: any[] = []; // gefüllt durch contentService.getPopularHerbs

  newsletterForm: FormGroup;
  submitted = false;

  // Statusmeldungen statt alert()
  newsletterSuccessMessage: string | null = null;
  newsletterErrorMessage: string | null = null;

  constructor(
    private seoService: SeoService,
    private contentService: ContentService,
    private blogService: BlogService,
    public imageService: ImageService,
    private newsletterService: NewsletterService,
    private fb: FormBuilder,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
    this.newsletterForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      consent: [false, Validators.requiredTrue]
    });
  }

  get email() {
    return this.newsletterForm.get('email');
  }

  get consent() {
    return this.newsletterForm.get('consent');
  }

  ngOnInit(): void {

    this.seoService.setTitle('Kräuter für Küche, Gesundheit & Wohlbefinden');
    this.seoService.setDescription(
      'Entdecke die vielseitige Welt der Kräuter: Finde die besten Kräuter für deine Küche, lerne über ihre gesundheitlichen Vorteile und erhalte wertvolle Tipps und Rezepte zur Verwendung von frischen und getrockneten Kräutern.'
    );
    this.seoService.setCanonicalUrl();
    this.seoService.setOpenGraphTags({
      title: `Kräuter für Küche, Gesundheit & Wohlbefinden | ${siteConfig.brand.displayName}`,
      description:
        'Entdecke die vielseitige Welt der Kräuter: Finde die besten Kräuter für deine Küche, lerne über ihre gesundheitlichen Vorteile und erhalte wertvolle Tipps und Rezepte zur Verwendung von frischen und getrockneten Kräutern.',
      image: siteConfig.seo.defaultImageUrl
    });
    this.seoService.setBreadcrumbSchema([{ name: 'Startseite', url: buildSiteUrl('/') }]);

    // Popular Herbs speichern
    this.popularHerbs = this.contentService.getPopularHerbs(7);

    // Blogposts laden
    this.loading = true;
    this.blogService.getLatestBlogPosts(3).subscribe({
      next: (posts) => {
        this.recentBlogPosts = posts;
        this.loading = false;
      },
      error: (err) => {
        console.error('Fehler beim Laden der neuesten Posts:', err);
        this.error = true;
        this.loading = false;
      }
    });
  }

  subscribeToNewsletter(): void {
    this.submitted = true;

    // Formularvalidierung
    if (!this.newsletterForm.valid) {
      this.newsletterErrorMessage = 'Bitte gib eine gültige E-Mail-Adresse an und bestätige die Datenschutzerklärung.';
      this.newsletterSuccessMessage = null;
      return;
    }

    const email = this.newsletterForm.get('email')?.value;

    // Newsletter-Anmeldung (alert() ersetzt durch status-driven UX)
    this.newsletterService.signUpNewsletter(email).subscribe({
      next: (response) => {
        console.log('Newsletter-Anmeldung erfolgreich:', response);
        this.newsletterSuccessMessage = 'Vielen Dank für deine Anmeldung zum Newsletter! Bitte bestätige die Anmeldung in der Bestätigungsmail.';
        this.newsletterErrorMessage = null;
        this.newsletterForm.reset({ email: '', consent: false });
        this.submitted = false;
      },
      error: (error) => {
        console.error('Fehler bei der Newsletter-Anmeldung:', error);
        this.newsletterErrorMessage = 'Es ist ein Fehler bei der Anmeldung aufgetreten. Bitte versuche es später erneut.';
        this.newsletterSuccessMessage = null;
      }
    });
  }

  formatDate(date: Date | string | undefined): string {
    if (!date) return '';

    const dateObj = new Date(date);
    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    };

    return dateObj.toLocaleDateString('de-DE', options);
  }

  getCategoryLabel(category: string | null | undefined): string {
    return getBlogCategoryDisplayName(category);
  }

  /**
   * SSR-safe: gibt reinen Text zurück (kein document-Zugriff im Server-Render).
   */
  getFormattedExcerpt(blog: BlogPost): string {
    if (!blog) return '';

    if (blog.description) {
      return this.truncateText(blog.description, 150);
    }

    if (blog.content) {
      const text = this.htmlToText(blog.content);
      return this.truncateText(text, 150);
    }

    return 'Mehr über diesen Beitrag erfahren...';
  }

  private htmlToText(html: string): string {
    if (!html) return '';

    if (isPlatformBrowser(this.platformId)) {
      // Browser: DOMParser verwenden (sicherer als direkt innerHTML auf ein Element zu setzen)
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        return (doc.body.textContent || '').trim();
      } catch (e) {
        // Fallback: einfache Tag-Strip-RegEx
        return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
      }
    }

    // Server-Fallback ohne DOM:
    return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  }

  truncateText(text: string, maxLength: number = 150): string {
    if (!text) return '';
    if (text.length <= maxLength) return text;

    let truncated = text.substring(0, maxLength);

    const lastSentence = truncated.lastIndexOf('.');
    if (lastSentence > maxLength * 0.7) {
      return truncated.substring(0, lastSentence + 1);
    }

    const lastSpace = truncated.lastIndexOf(' ');
    if (lastSpace > 0) truncated = truncated.substring(0, lastSpace);

    return truncated + '...';
  }
}



